/**
 * ChatMap Hills SP3b — HillChip (trail-target setter).
 *
 * Reads the session's TRAIL hill target from useHillTargets (the canonical
 * session_hill_targets table) and shows its resolved title; click opens the
 * HillTargetCombobox to match-or-create a Goal/Project/Task. setTarget → PUT
 * /hill-targets, which (single-writer, B7) also mirrors the legacy hill_to_climb
 * scalar so CouncilContext/wake-brief/recent_trails stay coherent.
 *
 * SP3a-era scalar editor (CouncilContext.setHillToClimb via a text input) is
 * retired here; the scalar path stays for legacy readers + propose_chip.
 */
import React, { useState } from 'react';
import { useHillTargets } from '../../hooks/useHillTargets';
import { HillTargetCombobox } from './HillTargetCombobox';

const PLACEHOLDER = 'set a hill to climb';

export function HillChip({ sessionId, className }: { sessionId?: string; className?: string }) {
  const { targets, setTarget } = useHillTargets(sessionId ?? null);
  const [editing, setEditing] = useState(false);
  const trail = targets.find((t) => t.scope_kind === 'trail' && (t.scope_ref ?? null) === null);
  // Free-text (label-only) trail hills have no entity → no title; still the
  // trail's stated hill, so show the label. (Nodes/drive only render entity-backed.)
  const title = trail?.title ?? trail?.label ?? null;

  if (editing && sessionId) {
    return (
      <div data-testid="hill-chip-editor" className={className}>
        <HillTargetCombobox
          current={title ? { title, source: 'trail' } : null}
          onPick={async (target) => { await setTarget({ scope_kind: 'trail', scope_ref: null, target }); setEditing(false); }}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid="hill-chip"
      onClick={() => setEditing(true)}
      aria-label={title ? `Hill: ${title}` : PLACEHOLDER}
      className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs
                  text-gray-600 dark:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-gray-800
                  transition-colors ${className ?? ''}`}
    >
      <span className="text-gray-500 dark:text-gray-400" aria-hidden>⛰</span>
      <span
        data-testid="hill-chip-text"
        className={`truncate max-w-[280px] ${title ? '' : 'italic text-gray-400 dark:text-gray-500'}`}
      >
        {title ?? PLACEHOLDER}
      </span>
    </button>
  );
}
